import React, { useState, useEffect, useMemo } from 'react';
import './DayBreakdown.css';
import MetricCard from '../MetricCard';
import CourtPanel from './CourtPanel';
import HourlyChart from './HourlyChart';
import { SPONSOR_LOGOS } from './constants';
import { fmtHM, formatDay } from './helpers';
import { useProcessedRows, useUniqueDays, useComputeMetrics } from './useComputeMetrics';

const DayBreakdown = ({ data, tournamentId, tournament }) => {
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedCourt, setSelectedCourt] = useState('');

  const rows = useProcessedRows(data);
  const uniqueDays = useUniqueDays(tournament, rows);
  const { computed, isComputing } = useComputeMetrics(rows, selectedDay, selectedCourt, uniqueDays);

  // Default to most recent day
  useEffect(() => {
    if (uniqueDays.length === 0) return;
    const last = uniqueDays[uniqueDays.length - 1];
    setSelectedDay(prev => (prev && uniqueDays.includes(prev) ? prev : last));
  }, [uniqueDays]);

  // Reset court when day changes
  useEffect(() => {
    setSelectedCourt('');
  }, [selectedDay]);

  // Collect all hour labels across sponsors for consistent x-axis
  const allHourLabels = useMemo(() => {
    if (!computed?.hourlyBreakdown) return [];
    const hours = new Set();
    Object.values(computed.hourlyBreakdown).forEach(hd => {
      Object.keys(hd).forEach(h => hours.add(h));
    });
    return [...hours].sort();
  }, [computed]);

  /* ── guards ── */
  if (tournamentId === 'all') {
    return (
      <div className="day-breakdown-prompt">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M3 9H21" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M8 2V6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M16 2V6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
        <p>Select a tournament above to view the day breakdown.</p>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return <div className="no-data">No data available for this tournament.</div>;
  }

  if (uniqueDays.length === 0) {
    return <div className="no-data">No date information found in the data.</div>;
  }

  /* ── render helpers ── */
  const renderSponsorCards = (perHour = false) => {
    if (!computed) return null;
    return computed.buckets.map(name => {
      const m = computed.sponsorMetrics[name];
      const count = perHour ? Math.round((m.count / computed.totalMatchMins) * 60) : m.count;
      const dur   = perHour ? (m.duration / computed.totalMatchMins) * 60 : m.duration;
      return (
        <MetricCard
          key={name + (perHour ? '-ph' : '')}
          title={name}
          value={`${count} GFX`}
          subtitle={fmtHM(dur)}
          icon={SPONSOR_LOGOS[name] || null}
        />
      );
    });
  };


  return (
    <div className="day-breakdown-container">
      {/* ── Page Header ── */}
      {computed && (
        <div className="day-breakdown-header">
          <h1 className="day-breakdown-tournament">{computed.tournamentName || 'Tournament'}</h1>
          <span className="day-breakdown-header-sep">|</span>
          <p className="day-breakdown-header-sub">Sponsor Dashboard</p>
          {selectedCourt && (
            <>
              <span className="day-breakdown-header-sep">|</span>
              <p className="day-breakdown-header-day">{selectedCourt}</p>
            </>
          )}
        </div>
      )}

      {/* ── Filters ── */}
      <div className="day-breakdown-filters">
        <select
          value={selectedDay || ''}
          onChange={(e) => setSelectedDay(e.target.value)}
          className="param-select day-breakdown-day-select"
          disabled={isComputing}
        >
          {uniqueDays.map((day, i) => (
            <option key={day} value={day}>
              Day {i + 1} — {formatDay(day)}
            </option>
          ))}
        </select>
        {computed && computed.courts && computed.courts.length > 0 && (
          <select
            value={selectedCourt}
            onChange={(e) => setSelectedCourt(e.target.value)}
            className="param-select day-breakdown-day-select"
            disabled={isComputing}
          >
            <option value="">All Courts</option>
            {computed.courts.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        )}
      </div>

      {/* ── Content ── */}
      {isComputing || !computed ? (
        <div className="day-breakdown-loader">
          <div className="day-breakdown-spinner" />
        </div>
      ) : computed.empty ? (
        <div className="day-breakdown-no-data">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 8v4m0 4h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
          </svg>
          <p>No data available for Day {computed.dayIndex}.</p>
          <span>Try selecting a different day from the dropdown above.</span>
        </div>
      ) : (
        <>
          {/* ── Executive Summary ── */}
          <div className="day-breakdown-section">
            <h2 className="day-breakdown-title">Executive Summary</h2>
            <h3 className="day-breakdown-subtitle">Total Sponsor Exposure</h3>
            <div className="metrics-grid">
              {renderSponsorCards(false)}
            </div>
          </div>

          {/* ── Average Total Per Live Hour ── */}
          <div className="day-breakdown-section">
            <h2 className="day-breakdown-title">Average Total Per Live Hour</h2>
            <div className="metrics-grid">
              {renderSponsorCards(true)}
            </div>
          </div>

          {/* ── Court Breakdown OR Hourly Breakdown ── */}
          {!selectedCourt ? (
            <>
              {computed.courts.length > 0 && (
                <div className="day-breakdown-section">
                  <h2 className="day-breakdown-title">Court Breakdown</h2>
                  <div className="court-panels-grid">
                    {computed.buckets.map(name => (
                      <CourtPanel key={name} bucketName={name}
                        courtData={computed.courtBreakdown[name]} courts={computed.courts} />
                    ))}
                  </div>
                </div>
              )}
              {computed.courts.length > 0 && (
                <div className="day-breakdown-section">
                  <h2 className="day-breakdown-title">Court Breakdown – Last 60 Minutes</h2>
                  <div className="court-panels-grid">
                    {computed.buckets.map(name => (
                      <CourtPanel key={name} bucketName={name}
                        courtData={computed.courtBreakdownLast60[name]} courts={computed.courts} />
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="day-breakdown-section">
              <h2 className="day-breakdown-title">Hourly Breakdown</h2>
              <div className="hourly-charts-grid">
                {computed.buckets.map(name => (
                  <HourlyChart key={name} bucketName={name}
                    hourData={computed.hourlyBreakdown[name]} allHourLabels={allHourLabels} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DayBreakdown;
